/**
 * §6.3 and §6.5 — matching payments to invoices, and what to do about the
 * difference when a payment falls short.
 *
 * THE LAST PHASE 3 EXIT CRITERION, AND IT HAD SLIPPED THREE TIMES
 *
 * §14's third criterion says the phase "ends with `detail_clearings`
 * writable", and is explicit that what Phase 3 adds is "the write path and the
 * interface". The write path has existed since Phase 2 — `POST /clearings`,
 * `POST /clearings/:id/void`, `POST /writeoffs`, the §7 limit trigger,
 * `control_line_balances`, `writeOffShortfall`. There was no interface, and
 * `api.ts` had no clearings method at all.
 *
 * WHAT A CLEARING NEEDS, AND WHY THIS SCREEN CANNOT CREATE IT ALONE
 *
 * BOTH SIDES POSTED. Migration 0010's trigger refuses a match where either
 * header is a draft, and putting an entry in the books is the operator's
 * decision, so posting happens in the editor. A payment that has not been posted does not
 * appear in the list here at all — which is why an operator who expects to see
 * a deposit and does not should look at its status before looking at this
 * screen.
 *
 * TWO CONTROL LINES, OPPOSITE SIDES. An invoice DEBITS the receivable; the
 * payment settling it CREDITS the same account. Both are `ar_control` lines on
 * posted headers, so the account role cannot tell them apart — the SIDE does,
 * and `control_line_balances` carries it so that every caller draws the
 * distinction identically. The two lists on this screen are the two sides.
 *
 * READ THE COLUMN THAT MATCHES THE LINE. Migration 0016's header is emphatic:
 * every row of that view carries four balance numbers and two are meaningless
 * for any given line. An invoice's own line has applied nothing, so its
 * "unapplied" figure equals the whole invoice and reads like a payment waiting
 * to be spent. Invoices show `outstandingCents`; payments show
 * `unappliedCents`. Swapping them raises nothing.
 *
 * §6.5 IS ASKED, NEVER INFERRED
 *
 * A short payment has two meanings that are numerically identical and
 * accounting-wise opposite:
 *
 *   A BANK FEE. The payer sent full value and the bank took a cut. The invoice
 *   is collected in full, and the difference is an ordinary expense line on the
 *   PAYMENT entry with its cash date set. That is done in the editor before
 *   posting, and there is deliberately no control for it here.
 *
 *   A WRITE-OFF. The payer will not pay the rest. The difference is bad debt
 *   and must be its own header with `cash_date` null (migration 0015 makes that
 *   a constraint). Recorded on the payment header instead, it would satisfy
 *   §6.5's conversion condition and emit cash-basis revenue for money that
 *   never arrived — with no error anywhere, months before anyone notices.
 *
 * §6.5 says the system cannot infer which applies. So there is no "write off
 * the difference" checkbox on the apply action: the write-off is a separate,
 * named thing the operator opens, and the form says what the other answer is
 * so choosing this one is a decision rather than the only button on screen.
 *
 * THE SERVER IS THE AUTHORITY ON EVERY REFUSAL
 *
 * Over-allocation is a trigger (0010 and 0011), and it holds against writers
 * this screen knows nothing about. The warnings below are shown so the
 * operator sees the problem while typing rather than after submitting, and the
 * Apply button stays ENABLED when they fire — the same choice EditorPage makes
 * for a bank-amount mismatch. A disabled button on a client-side sum is how a
 * legal action becomes unreachable because of an arithmetic bug in a screen.
 */

import { AccessNotice } from '../components/AccessNotice.js';
import { projectWord, useBusiness, useCan } from '../auth/AuthProvider.js';
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ReceivablesTabs } from '../components/ReceivablesTabs.js';
import { Toast } from '../components/Toast.js';
import {
  ApiError,
  api,
  type Account,
  type OpenInvoice,
  type OpenInvoicesResult,
  type PaymentClearing,
  type Project,
  type UnappliedPayment,
  type UnappliedPaymentsResult,
} from '../api.js';
import { formatAmountInput, formatCents, parseCents } from '../lib/money.js';

/** What the write-off form holds while it is open. */
interface WriteOffDraft {
  invoice: OpenInvoice;
  amount: string;
  expenseAccountId: string;
  entryDate: string;
  memo: string;
  /** 0081: the program (project) charged, where the invoice's lines name none; empty is the invoice's. */
  projectId: string;
}

export function ClearingsPage() {
  const mayEdit = useCan()('edit_entries');
  const [payments, setPayments] = useState<UnappliedPaymentsResult | null>(null);
  const [invoices, setInvoices] = useState<OpenInvoicesResult | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);

  // A customer passed from the Invoices screen's Match a Payment link starts
  // both searches, so the deposit and the invoice are already side by side.
  const [searchParams] = useSearchParams();
  const [paymentSearch, setPaymentSearch] = useState(() => searchParams.get('customer') ?? '');
  const [invoiceSearch, setInvoiceSearch] = useState(() => searchParams.get('customer') ?? '');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [matches, setMatches] = useState<PaymentClearing[]>([]);

  /**
   * What the operator has typed, keyed by PAYMENT and then by the invoice's
   * A/R line.
   *
   * Raw text rather than cents, for the reason the editor's amount fields are:
   * a half-typed "12." must survive the next keystroke, and parsing on every
   * render would destroy it.
   *
   * KEYED BY PAYMENT, AND THAT IS THE WHOLE FIX (2026-09-15).
   *
   * It used to be one flat map, emptied whenever the selection changed. A
   * deposit day is several payments against an overlapping set of invoices,
   * and the operator works it by laying out the whole thing before applying
   * any of it -- fill against payment A, click payment B to check what is left
   * on it, and A's figures were gone. Not saved somewhere and not warned
   * about: erased, by the act of looking at the next payment. The only way
   * through was to apply each payment before looking at the next one, which
   * is the opposite of how the work is actually done.
   *
   * Nothing here is sent until Apply, and Apply still sends ONE payment: the
   * server's allocation call is per payment and the triggers that cap a
   * clearing are per payment. This is a scratchpad that survives a click, not
   * a batch.
   */
  const [amountsByPayment, setAmountsByPayment] = useState<
    Record<string, Record<string, string>>
  >({});

  const [undoing, setUndoing] = useState<{ id: string; reason: string } | null>(null);
  const [writeOff, setWriteOff] = useState<WriteOffDraft | null>(null);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const message = (caught: unknown, fallback: string) =>
    caught instanceof ApiError ? caught.message : fallback;

  const loadLists = useCallback(async () => {
    setLoading(true);
    try {
      const [paymentResult, invoiceResult] = await Promise.all([
        api.listUnappliedPayments({ counterparty: paymentSearch.trim() || undefined }),
        api.listOpenInvoices({ counterparty: invoiceSearch.trim() || undefined }),
      ]);
      setPayments(paymentResult);
      setInvoices(invoiceResult);
      setError(null);
    } catch (caught) {
      setError(message(caught, 'Could not load payments and invoices.'));
    } finally {
      setLoading(false);
    }
  }, [paymentSearch, invoiceSearch]);

  useEffect(() => {
    void loadLists();
  }, [loadLists]);

  /*
   * How many invoices are still drafts. They cannot be matched until posted, and
   * a deposit whose invoice is a draft looks, from here, like a deposit with no
   * invoice at all -- which is exactly how the Synergy Computer receivables were
   * misread. A count is enough; the list is on the Invoices tab.
   */
  const [draftInvoices, setDraftInvoices] = useState(0);
  useEffect(() => {
    api
      .listInvoices({ status: 'draft', limit: 1 })
      .then((result) => setDraftInvoices(result.counts.draft))
      .catch(() => setDraftInvoices(0));
  }, []);

  // 0081 (§A.4): the program a write-off is charged to, where the invoice
  // gives none. A non-profit's must have one; the server says so if it lacks it.
  const business = useBusiness();
  const projectLabel = projectWord(business);
  const [projects, setProjects] = useState<Project[]>([]);
  useEffect(() => {
    api
      .listProjects()
      .then((result) => setProjects(result.projects.filter((project) => project.archivedAt === null)))
      .catch(() => setProjects([]));
  }, []);

  // Once. The write-off form needs somewhere to put the expense, and the chart
  // does not change while this screen is open.
  useEffect(() => {
    api
      .listAccounts()
      .then((result) => setAccounts(result.accounts))
      .catch((caught: unknown) => setError(message(caught, 'Could not load the chart.')));
  }, []);

  const selected: UnappliedPayment | undefined = payments?.payments.find(
    (row) => row.detailId === selectedId,
  );

  /*
   * The matches already applied from the selected payment.
   *
   * Cleared before the request rather than after, so a payment with no matches
   * never shows the previous payment's -- which would read as money applied
   * from a deposit it did not come from.
   */
  useEffect(() => {
    setMatches([]);
    setUndoing(null);
    setWriteOff(null);
    // The typed amounts deliberately survive this. See `amountsByPayment`.
    if (selectedId === null) return;
    let cancelled = false;
    api
      .listPaymentClearings(selectedId)
      .then((result) => {
        if (cancelled) return;
        setMatches(result.clearings);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(message(caught, 'Could not list what this payment has been applied to.'));
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  /**
   * The allocations as they currently stand, and whether any of them is a
   * number at all.
   *
   * `parseCents` returns null for junk and 0 for an empty field, and the two
   * are different: a blank row is a row with no allocation, and "abc" is a
   * mistake the operator has to be shown.
   */
  /** This payment's own scratchpad, and a setter that writes back into it. */
  const amounts = (selectedId !== null ? amountsByPayment[selectedId] : undefined) ?? {};
  const setAmounts = (next: Record<string, string>) => {
    if (selectedId === null) return;
    setAmountsByPayment((current) => ({ ...current, [selectedId]: next }));
  };

  /**
   * What OTHER payments already have typed against a given invoice.
   *
   * Without this the two halves of a simultaneous match cannot see each other:
   * Fill on payment A offers the invoice's whole outstanding, the operator
   * clicks payment B and Fill offers the same whole outstanding again, and the
   * second Apply is refused by migration 0010's trigger -- correctly, and long
   * after the mistake was made. Subtracting it means Fill never offers money
   * that is already spoken for on this screen.
   *
   * It is a screen-level guard and not a rule: the trigger is still what makes
   * it safe, and this only stops the operator being walked into it.
   */
  function pendingElsewhereCents(invoiceDetailId: string): number {
    return Object.entries(amountsByPayment).reduce((sum, [paymentId, rows]) => {
      if (paymentId === selectedId) return sum;
      return sum + Math.max(0, parseCents(rows[invoiceDetailId] ?? '') ?? 0);
    }, 0);
  }

  /** Total typed against a payment, for the figure on its row in the list. */
  function pendingForPayment(paymentId: string): number {
    return Object.values(amountsByPayment[paymentId] ?? {}).reduce(
      (sum, text) => sum + Math.max(0, parseCents(text) ?? 0),
      0,
    );
  }

  const entered = Object.entries(amounts)
    .map(([invoiceDetailId, text]) => ({
      invoiceDetailId,
      text,
      cents: parseCents(text),
    }))
    .filter((row) => row.text.trim() !== '');

  const unparseable = entered.filter((row) => row.cents === null);
  const allocations = entered
    .filter((row): row is typeof row & { cents: number } => row.cents !== null && row.cents > 0)
    .map((row) => ({ invoiceDetailId: row.invoiceDetailId, amountCents: row.cents }));

  const allocatedCents = allocations.reduce((sum, row) => sum + row.amountCents, 0);
  const remainingCents = (selected?.unappliedCents ?? 0) - allocatedCents;

  /**
   * §6.3 — what the fill button offers on one invoice row.
   *
   * The SMALLER of what this invoice still owes and what is left of the
   * payment AFTER the other rows have taken their share. Both limits are
   * database triggers -- 0010 caps a clearing at the line it settles, 0011
   * caps it at the payment's own value -- so an offer bounded by both can
   * never propose something that will be refused.
   *
   * `remainingCents` already subtracts what is typed into every row including
   * this one, so this row's own entry is added back before comparing.
   * Otherwise pressing Fill twice would offer less the second time.
   *
   * Null when there is nothing left to offer, which is what hides the button.
   */
  function fillFor(invoice: OpenInvoice): number | null {
    const mine = parseCents(amounts[invoice.detailId] ?? '') ?? 0;
    const available = remainingCents + mine;
    // What this invoice still owes AFTER the other payments on this screen
    // have taken their share. See `pendingElsewhereCents`.
    const owed = invoice.outstandingCents - pendingElsewhereCents(invoice.detailId);
    const offer = Math.min(owed, available);
    if (offer <= 0 || offer === mine) return null;
    return offer;
  }


  /*
   * Two warnings, both of which the server would also raise. Shown here so the
   * problem appears while the number is being typed rather than after Apply,
   * and NOT used to disable the button -- see the header.
   */
  const overPayment = selected !== undefined && allocatedCents > selected.unappliedCents;
  const overInvoice = allocations.filter((row) => {
    const invoice = invoices?.invoices.find(
      (candidate) => candidate.detailId === row.invoiceDetailId,
    );
    return invoice !== undefined && row.amountCents > invoice.outstandingCents;
  });

  async function applyPayment() {
    if (selected === undefined || allocations.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const { allocation } = await api.allocatePayment({
        paymentDetailId: selected.detailId,
        allocations,
      });
      setNotice(
        `Applied ${formatCents(allocation.totalAllocatedCents)}. ` +
          `${formatCents(allocation.paymentRemainingCents)} of this payment is left.`,
      );
      // Only THIS payment's scratchpad. The figures laid out against the other
      // payments are still wanted -- that is what the operator came to do.
      setAmountsByPayment((current) => {
        const next = { ...current };
        delete next[selected.detailId];
        return next;
      });
      /*
       * Both lists AND the match list. An allocation changes what is open on
       * one side and what is unapplied on the other, and a screen that
       * refreshed only the side it wrote to would leave the operator matching
       * against a figure the server no longer agrees with.
       */
      await loadLists();
      const refreshed = await api.listPaymentClearings(selected.detailId);
      setMatches(refreshed.clearings);
    } catch (caught) {
      setError(message(caught, 'Could not apply that payment.'));
    } finally {
      setBusy(false);
    }
  }

  async function undoMatch() {
    if (undoing === null || undoing.reason.trim() === '' || selected === undefined) return;
    setBusy(true);
    setError(null);
    try {
      await api.voidClearing(undoing.id, undoing.reason.trim());
      setNotice('Match undone. The payment and the invoice are both open again.');
      setUndoing(null);
      await loadLists();
      const refreshed = await api.listPaymentClearings(selected.detailId);
      setMatches(refreshed.clearings);
    } catch (caught) {
      setError(message(caught, 'Could not undo that match.'));
    } finally {
      setBusy(false);
    }
  }

  async function submitWriteOff() {
    if (writeOff === null || selected === undefined) return;
    const cents = parseCents(writeOff.amount);
    if (cents === null || cents <= 0) {
      setError('The write-off amount is not a number of dollars.');
      return;
    }
    if (writeOff.expenseAccountId === '') {
      setError('Choose the account the write-off is charged to.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { writeOff: result } = await api.writeOffShortfall({
        paymentHeaderId: selected.headerId,
        // No receivable account: the server credits the INVOICE line's own
        // (review issue #30). Charging the write-off to a different receivable
        // than the one it settles would balance and would be wrong, so it is
        // not the caller's to say.
        invoiceDetailId: writeOff.invoice.detailId,
        expenseAccountId: writeOff.expenseAccountId,
        amountCents: cents,
        entryDate: writeOff.entryDate,
        ...(writeOff.memo.trim() !== '' && { memo: writeOff.memo.trim() }),
        ...(writeOff.projectId !== '' && { projectId: writeOff.projectId }),
      });
      setNotice(
        `Wrote off ${formatCents(result.amountCents)}. ` +
          'It is a posted entry of its own, with no cash date.',
      );
      setWriteOff(null);
      await loadLists();
    } catch (caught) {
      setError(message(caught, 'Could not write that off.'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Expense accounts, for the write-off.
   *
   * Filtered to expenses because a write-off is an expense — but not narrowed
   * to an account NAMED "bad debt", which would be guessing at a chart this
   * screen does not own. Inactive accounts are excluded for the reason the
   * editor's picker excludes them: they are not available to code to.
   */
  const expenseAccounts = accounts.filter(
    (account) => account.type === 'expense' && account.isActive,
  );

  return (
    <section className="clearings">
      <header className="row triage-head">
        <h1>Matching</h1>
        <span className="muted">
          {loading
            ? 'Loading…'
            : `${formatCents(payments?.totalUnappliedCents ?? 0)} unapplied · ` +
              `${formatCents(invoices?.totalOutstandingCents ?? 0)} outstanding`}
        </span>
      </header>
      <AccessNotice capability="edit_entries" what="payments and their matches" />

      <ReceivablesTabs />

      {draftInvoices > 0 && (
        <p className="notice">
          {draftInvoices === 1 ? '1 invoice is' : `${draftInvoices} invoices are`} still a draft and
          can't be matched until posted. <Link to="/invoices?status=draft">See Drafts</Link>
        </p>
      )}

      {notice && (
        <Toast message={notice} onDismiss={() => setNotice(null)} />
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {/*
        §6.3 — three grid children, not two.

        The filter used to sit inside a wrapper around the list, which put the
        list one control lower than the pane beside it and left the pane's
        first line floating against nothing. Unwrapped, the filter takes row
        one and the list and the pane both take row two, so they start level by
        construction rather than by a margin guessing at a control's height.
      */}
      <div className="triage-body">
          {/*
            HEADINGS ON BOTH PANES (2026-09-15).
            
            The screen was two unlabelled columns: a list on the left and a
            form on the right, with nothing saying what either one was or which
            way the money flows between them. The operator had to infer it from
            the contents, which works only once you already know. `aria-label`
            on the list said it, but only to a screen reader -- the sighted
            operator got nothing at all.
          */}
          <div className="pane-head">
            <h2>Unapplied Payments</h2>
            <span className="muted">Pick one, then say which invoices it settles.</span>
          </div>
          <div className="row triage-filters payments-filter">
            <label>
              Payments{' '}
              <input
                type="search"
                aria-label="Search payments"
                placeholder="counterparty"
                value={paymentSearch}
                onChange={(event) => setPaymentSearch(event.target.value)}
              />
            </label>
          </div>

          {/*
            Only payments with money still to apply. A fully applied deposit is
            not hidden by the view -- 0016 carries every posted control line,
            settled ones included -- so this list is already the answer to
            "what is left to do", and an empty one means there is nothing.
          */}
          <ol className="queue payments-list" aria-label="Unapplied payments">
            {(payments?.payments ?? []).map((payment) => (
              <li
                key={payment.detailId}
                className={`queue-row${payment.detailId === selectedId ? ' is-cursor' : ''}`}
                aria-current={payment.detailId === selectedId}
                onClick={() => setSelectedId(payment.detailId)}
              >
                <span className="queue-date">{payment.entryDate}</span>
                <span className="queue-party">
                  {payment.counterparty ?? <em>no counterparty</em>}
                </span>
                <span className="queue-amount">{formatCents(payment.unappliedCents)}</span>
                {(() => {
                  const pending = pendingForPayment(payment.detailId);
                  if (payment.appliedCents === 0 && pending === 0) return null;
                  /*
                    What qualifies the amount, on a line of its own beneath it
                    (the row has three columns and no room for a fourth: the
                    badge in one was clipped, reported 2026-10-16).
                  */
                  return (
                    <span className="payment-extras">
                      {/*
                        The whole payment, shown only when part of it is already
                        applied. On an untouched deposit the two numbers are equal
                        and the second one says nothing.
                      */}
                      {payment.appliedCents > 0 && (
                        <span className="queue-state muted">of {formatCents(payment.originalCents)}</span>
                      )}
                      {/*
                        What is typed against this payment but not yet applied. This
                        is what makes laying out several payments before applying any
                        of them legible: without it, work done on a payment the
                        operator is not currently looking at is invisible, and the
                        list gives no reason to believe it is still there.
                      */}
                      {pending > 0 && (
                        <span className="queue-badge pending-badge">{formatCents(pending)} drafted</span>
                      )}
                    </span>
                  );
                })()}
              </li>
            ))}
            {!loading && (payments?.payments.length ?? 0) === 0 && (
              <li className="queue-empty muted">
                Nothing to apply. A payment appears here once it is POSTED and has
                money left on its receivable line.
              </li>
            )}
          </ol>

        <aside className="detail-pane" aria-label="Payment detail">
          {selected === undefined ? (
            <>
              <div className="pane-head">
                <h2>This Payment</h2>
              </div>
              <p className="muted">Select a payment from the list.</p>
            </>
          ) : (
            <>
              <div className="pane-head">
                <span className="muted pane-eyebrow">This Payment</span>
              </div>
              <h2>{selected.counterparty ?? 'No counterparty'}</h2>
              <dl className="facts">
                <div className="fact">
                  <dt>Payment date</dt>
                  <dd>{selected.entryDate}</dd>
                </div>
                <div className="fact">
                  <dt>Payment</dt>
                  <dd>{formatCents(selected.originalCents)}</dd>
                </div>
                <div className="fact">
                  <dt>Applied</dt>
                  <dd>{formatCents(selected.appliedCents)}</dd>
                </div>
                <div className="fact">
                  <dt>Still to apply</dt>
                  <dd>{formatCents(selected.unappliedCents)}</dd>
                </div>
              </dl>

              {/*
                What this payment has already settled.

                LIVE MATCHES ONLY. A voided one still exists -- §6.3 says a
                match is undone with a reason and never deleted -- but it moves
                no balance, so listing it beside matches that do would put rows
                that change the number next to rows that do not. The history is
                in the audit log.
              */}
              {matches.length > 0 && (
                <>
                  <h3>Applied to</h3>
                  <ul className="match-list" aria-label="Applied to">
                    {matches.map((match) => (
                      <li key={match.id}>
                        <span>
                          {match.invoiceEntryDate}{' '}
                          <Link to={`/journals/${match.invoiceHeaderId}`}>
                            {match.invoiceReference ?? match.counterparty ?? 'invoice'}
                          </Link>
                        </span>
                        <span className="num">{formatCents(match.amountCents)}</span>
                        {mayEdit && (
                          <button
                            type="button"
                            className="link"
                            aria-label={`Undo ${formatCents(match.amountCents)} applied to ${
                              match.invoiceReference ?? match.invoiceEntryDate
                            }`}
                            onClick={() => setUndoing({ id: match.id, reason: '' })}
                          >
                            Undo
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/*
                §6.3 — a match is undone with a REASON, which the server
                requires and which is the audit trail. Asked for in a field
                rather than defaulted to something like "corrected": a stored
                reason nobody chose is worse than none, because it reads as
                though somebody explained themselves.
              */}
              {undoing !== null && (
                <div className="new-account undo-form">
                  <label className="field">
                    <span>Why is this being undone?</span>
                    <input
                      aria-label="Reason for undoing the match"
                      value={undoing.reason}
                      onChange={(event) =>
                        setUndoing({ ...undoing, reason: event.target.value })
                      }
                    />
                  </label>
                  <div className="row">
                    <button
                      type="button"
                      disabled={busy || undoing.reason.trim() === ''}
                      onClick={() => void undoMatch()}
                    >
                      Undo the match
                    </button>
                    <button type="button" className="link" onClick={() => setUndoing(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <h3>Open invoices</h3>
              <div className="row triage-filters">
                <label>
                  Search{' '}
                  <input
                    type="search"
                    aria-label="Search invoices"
                    placeholder="counterparty"
                    value={invoiceSearch}
                    onChange={(event) => setInvoiceSearch(event.target.value)}
                  />
                </label>
              </div>

              <table className="lines invoice-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th className="num">Outstanding</th>
                    {mayEdit && <th className="num">Apply</th>}
                    {mayEdit && <th />}
                  </tr>
                </thead>
                <tbody>
                  {(invoices?.invoices ?? []).map((invoice) => (
                    <tr key={invoice.detailId}>
                      <td>
                        {invoice.entryDate}{' '}
                        {invoice.invoiceReference ?? invoice.counterparty ?? '—'}
                        {/*
                          Age, not a due date. The schema carries no payment
                          terms, so nothing here may imply a deadline -- see
                          the receivables service.
                        */}
                        <span className="muted"> · {invoice.bucket} days</span>
                      </td>
                      <td className="num">{formatCents(invoice.outstandingCents)}</td>
                      {mayEdit && (
                        <td className="num">
                          <input
                            inputMode="decimal"
                            aria-label={`Apply to ${
                              invoice.invoiceReference ?? invoice.entryDate
                            }`}
                            value={amounts[invoice.detailId] ?? ''}
                            onChange={(event) =>
                              setAmounts({
                                ...amounts,
                                [invoice.detailId]: event.target.value,
                              })
                            }
                          />
                          {/*
                            §6.3 — the amount, offered rather than typed.

                            Mirrors the editor's fill button, and for the same
                            reason: this number is arithmetic, not a decision. It
                            is whichever is SMALLER of what the invoice still
                            owes and what the payment has left, because applying
                            more than either is what migrations 0010 and 0011
                            refuse -- so the offer can never propose something
                            the database will reject.

                            A BUTTON, not a prefill. A value that appears on its
                            own is the field that stops being read, and applying
                            a payment across several invoices is exactly where
                            the operator has to look.
                          */}
                          {fillFor(invoice) !== null && (
                            <button
                              type="button"
                              className="link fill-apply"
                              onClick={() =>
                                setAmounts({
                                  ...amounts,
                                  [invoice.detailId]: formatAmountInput(fillFor(invoice)!),
                                })
                              }
                            >
                              Fill {formatCents(fillFor(invoice)!)}
                            </button>
                          )}
                        </td>
                      )}
                      {mayEdit && (
                        <td className="write-off-cell">
                          {/*
                            §6.5 — a separate, named action, never a checkbox on
                            Apply. The form below says what the other answer is.
                          */}
                          <button
                            type="button"
                            className="link"
                            onClick={() =>
                              setWriteOff({
                                invoice,
                                amount: (invoice.outstandingCents / 100).toFixed(2),
                                expenseAccountId: '',
                                projectId: '',
                                /*
                                  Dated from the PAYMENT, not from the browser.
                                  §5.4: the tenant's today and the browser's are
                                  different days for several hours of every day,
                                  and the client has no way to know the tenant's
                                  timezone. Rather than guess, this starts from a
                                  date the server supplied and the operator can
                                  change it.
                                */
                                entryDate: selected.entryDate,
                                memo: '',
                              })
                            }
                          >
                            Write off…
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {!loading && (invoices?.invoices.length ?? 0) === 0 && (
                    <tr>
                      <td colSpan={mayEdit ? 4 : 2} className="muted">
                        No open invoices. One appears here once it is POSTED and has
                        an unsettled receivable line.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/*
                The rail, and it appears only when something is wrong or
                something has been entered -- the same rule the editor's
                balance rail follows. A permanent "$0.00 allocated" line on an
                untouched screen is noise.
              */}
              {(allocations.length > 0 || unparseable.length > 0) && (
                <p className={`rail${overPayment || unparseable.length > 0 ? ' rail-off' : ''}`}>
                  {unparseable.length > 0 ? (
                    <>Some amounts are not numbers of dollars.</>
                  ) : overPayment ? (
                    <>
                      Applying {formatCents(allocatedCents)} of{' '}
                      {formatCents(selected.unappliedCents)} available — over by{' '}
                      {formatCents(allocatedCents - selected.unappliedCents)}. The server
                      will refuse this.
                    </>
                  ) : (
                    <>
                      Applying {formatCents(allocatedCents)}, leaving{' '}
                      {formatCents(remainingCents)} on this payment.
                    </>
                  )}
                </p>
              )}

              {overInvoice.length > 0 && (
                <p className="rail rail-off">
                  {overInvoice.length === 1
                    ? 'One amount is more than that invoice is still owed.'
                    : `${overInvoice.length} amounts are more than those invoices are still owed.`}{' '}
                  The server will refuse this.
                </p>
              )}

              {mayEdit && (
                <div className="row">
                  <button
                    type="button"
                    disabled={busy || allocations.length === 0}
                    onClick={() => void applyPayment()}
                  >
                    {busy ? 'Applying…' : 'Apply'}
                  </button>
                </div>
              )}

              {writeOff !== null && (
                <div className="new-account writeoff-form">
                  <h3>Write off the rest of this invoice</h3>
                  {/*
                    §6.5, stated where the decision is made. The operator is
                    choosing between two records that look identical in dollars
                    and differ on a cash-basis report months later, so the other
                    answer has to be on screen rather than in a manual.
                  */}
                  <p className="muted">
                    This says the money will never arrive. If the payer sent full value
                    and the bank took a cut, that is a BANK FEE — close this, add the
                    fee as an expense line on the payment entry in the editor, and let
                    the payment clear the invoice in full. A write-off becomes its own
                    posted entry with no cash date, matched to the invoice.
                  </p>
                  <label className="field">
                    <span>Amount</span>
                    <input
                      aria-label="Write-off amount"
                      inputMode="decimal"
                      value={writeOff.amount}
                      onChange={(event) =>
                        setWriteOff({ ...writeOff, amount: event.target.value })
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Charged to</span>
                    <select
                      aria-label="Write-off account"
                      value={writeOff.expenseAccountId}
                      onChange={(event) =>
                        setWriteOff({ ...writeOff, expenseAccountId: event.target.value })
                      }
                    >
                      <option value="">Choose an account…</option>
                      {expenseAccounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.code} {account.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  {projects.length > 0 && (
                    <label className="field">
                      <span>{projectLabel}</span>
                      <select
                        aria-label={`Write-off ${projectLabel.toLowerCase()}`}
                        value={writeOff.projectId}
                        onChange={(event) => {
                          const projectId = event.target.value;
                          setWriteOff((current) => (current === null ? current : { ...current, projectId }));
                        }}
                      >
                        <option value="">The Invoice&apos;s</option>
                        {projects.map((project) => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  <label className="field">
                    <span>Date</span>
                    <input
                      type="date"
                      aria-label="Write-off date"
                      value={writeOff.entryDate}
                      onChange={(event) =>
                        setWriteOff({ ...writeOff, entryDate: event.target.value })
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Memo</span>
                    <input
                      aria-label="Write-off memo"
                      value={writeOff.memo}
                      onChange={(event) =>
                        setWriteOff({ ...writeOff, memo: event.target.value })
                      }
                    />
                  </label>
                  <div className="row">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void submitWriteOff()}
                    >
                      Write it off
                    </button>
                    <button type="button" className="link" onClick={() => setWriteOff(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </aside>
      </div>
    </section>
  );
}
